import {
  CLEAR_TELEMETRY_DATA,
  GET_AIRCRAFT_SUCCESS,
  UPDATE_LIVE_AIRCRAFT_DATA,
  UPDATE_PILOT_DATA,
  ADD_MISSION_TO_AIRCRAFT_PENDING,
  ADD_MISSION_TO_AIRCRAFT_FULFILLED,
  ADD_MISSION_TO_AIRCRAFT_FAILED,
  MISSION_STOPPED,
  MISSION_STARTED,
  TRIGGER_UAV_EVENT,
  END_UAV_EVENT,
  CLEAR_AIRCRAFT,
  TOGGLE_GEOTUNNEL,
  TOGGLE_SHOW_SITL,
  CHOOSE_LOCATION,
  CHOOSE_AIRCRAFT,
  GROUND_TEAM_DATA,
  UPDATE_CLIENTS,
  GET_LANDING_PADS_SUCCESS,
  LINK_PAD_WITH_CHANNEL,
  UPDATE_LANDING_PAD_SUCCESS,
  LINK_PAD_WITH_AIRCRAFT_SUCCESS,
  REMOVE_AIRCRAFT_FROM_PAD_SUCCESS
} from '../actions/actionTypes';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web

/**
 * Default state for live aircraft reducer
 * @typedef {Object} LiveAircraftState
 * @property {Array} aircraft - Aircraft data array
 * @property {Object} liveData - Live aircraft telemetry data by UAV ID
 * @property {Object} missions - Mission data by UAV ID
 * @property {boolean} showGeotunnels - Whether to show geotunnels
 * @property {boolean} showSitl - Whether to show SITL data
 * @property {Array} clients - Client data array
 * @property {Object} padConfig - Pad configuration by pad ID
 * @property {import('@m4a3/api-client').LandingPad[]} pads - All landing pads from API
 * @property {import('@m4a3/api-client').LandingPad[]} activePads - Active landing pads only
 * @property {import('@m4a3/api-client').LandingPad[]} inactivePads - Inactive landing pads only
 */

/**
 * @type {LiveAircraftState}
 */
const defaultState = {
  aircraft: [],
  liveData: {},
  missions: {},
  showGeotunnels: false,
  showSitl: false,
  clients: [],
  padConfig: {},
  pads: [], // Populated with LandingPad[] from /addressfind/listLandingPads API
  activePads: [], // Filtered from pads[] where active === true
  inactivePads: [] // Filtered from pads[] where active === false
};

const reducer = (state = defaultState, action) => {
  switch (action.type) {
    case GET_AIRCRAFT_SUCCESS: {
      return {
        ...state,
        aircraft: action.payload
      };
    }
    case MISSION_STOPPED: {
      const { uavId } = action.payload;
      if (!uavId) return state;
      return {
        ...state,
        missions: {
          ...state.missions,
          [uavId]: {}
        }
      };
    }
    case MISSION_STARTED: {
      return state;
    }
    case UPDATE_PILOT_DATA: {
      const { uavId, pilotData } = action.payload;
      const existingAircraftData = state.liveData?.[uavId];

      if (!uavId) return state;
      return {
        ...state,
        liveData: {
          ...state.liveData,
          [uavId]: {
            ...existingAircraftData,
            pilotData
          }
        }
      };
    }
    case CLEAR_TELEMETRY_DATA: {
      return {
        ...state,
        liveData: {}
      };
    }
    case UPDATE_LIVE_AIRCRAFT_DATA: {
      return {
        ...state,
        liveData: action.payload
      };
    }
    case ADD_MISSION_TO_AIRCRAFT_PENDING: {
      const { uavId } = action.payload;
      if (!uavId) return state;
      const missionData = state.missions?.[uavId] || {};
      return {
        ...state,
        missions: {
          ...state.missions,
          [uavId]: {
            ...missionData,
            phoneId: uavId,
            route: null,
            geoTunnel: undefined,
            fetching: true
          }
        }
      };
    }
    case ADD_MISSION_TO_AIRCRAFT_FULFILLED: {
      const { uavId, route, geoTunnel, missionId } = action.payload;
      if (!uavId) return state;
      const missionData = state.missions?.[uavId] || {};
      return {
        ...state,
        missions: {
          ...state.missions,
          [uavId]: {
            ...missionData,
            missionId,
            route,
            geoTunnel,
            fetching: false
          }
        }
      };
    }
    case ADD_MISSION_TO_AIRCRAFT_FAILED: {
      const { uavId } = action.payload;
      if (!uavId) return state;
      const missionData = state.missions?.[uavId] || {};

      return {
        ...state,
        missions: {
          ...state.missions,
          [uavId]: {
            ...missionData,
            route: undefined,
            geoTunnel: undefined,
            fetching: false
          }
        }
      };
    }
    case TRIGGER_UAV_EVENT: {
      const { uavId, id, message } = action.payload;
      const existingAircraftData = state.liveData?.[uavId] || {};
      return {
        ...state,
        liveData: {
          ...state.liveData,
          [uavId]: {
            ...existingAircraftData,
            uavEvent: { message, id }
          }
        }
      };
    }
    case END_UAV_EVENT: {
      return {
        ...state,
        liveData: action.payload
      };
    }
    case CLEAR_AIRCRAFT: {
      const { uavId } = action.payload;
      if (!uavId || !state.liveData[uavId]) return state;
      const { [uavId]: _, ...rest } = state.liveData || {};
      return {
        ...state,
        liveData: {
          ...rest
        }
      };
    }
    case TOGGLE_GEOTUNNEL: {
      return { ...state, showGeotunnels: !state.showGeotunnels };
    }
    case TOGGLE_SHOW_SITL: {
      return { ...state, showSitl: !state.showSitl };
    }
    case CHOOSE_LOCATION: {
      return {
        ...state,
        location: action.payload
      };
    }
    case CHOOSE_AIRCRAFT: {
      return {
        ...state,
        chosenAircraft: action.payload
      };
    }
    case GROUND_TEAM_DATA: {
      const clients = state.clients.map(client => {
        const locationData = action?.payload?.data?.find(team => {
          return team?.id === client.id;
        });

        return {
          ...client,
          ...locationData
        };
      });
      return { ...state, clients };
    }
    case UPDATE_CLIENTS: {
      return {
        ...state,
        clients: action.payload
      };
    }
    case GET_LANDING_PADS_SUCCESS: {
      return {
        ...state,
        pads: action.payload,
        activePads: action.payload.filter(p => p.active === true),
        inactivePads: action.payload.filter(p => p.active === false)
      };
    }
    case UPDATE_LANDING_PAD_SUCCESS: {
      const updatedLandingPad = action.payload;
      const landingPadsWithoutUpdatedOne = state.pads.filter(p => p.id !== updatedLandingPad.id);
      const updatedPads = [...landingPadsWithoutUpdatedOne, updatedLandingPad];
      console.log('UPDATE_LANDING_PAD_SUCCESS', updatedPads, action.payload)
      return {
        ...state,
        pads: updatedPads,
        activePads: updatedPads.filter(p => p.active === true),
        inactivePads: updatedPads.filter(p => p.active === false)
      };
    }
    case LINK_PAD_WITH_AIRCRAFT_SUCCESS: {
      const { pad, aircraft } = action.payload;
      const padConfig = state.padConfig?.[pad] || {};
      const updatedPads = state.pads.map(p => {
        if (p.id === pad) {
          p.aircraft = aircraft;
        }
        return p;
      });
      return {
        ...state,
        pads: updatedPads,
        activePads: updatedPads.filter(p => p.active === true),
        inactivePads: updatedPads.filter(p => p.active === false),
        padConfig: {
          ...state.padConfig,
          [pad]: {
            ...padConfig,
            id: pad,
            aircraft
          }
        }
      };
    }
    case REMOVE_AIRCRAFT_FROM_PAD_SUCCESS: {
      const { pad, aircraft } = action.payload;
      const padConfig = state.padConfig?.[pad] || {};
      const updatedPads = state.pads.map(p => {
        if (p.id === pad) {
          p.aircraft = 0;
        }
        return p;
      });
      return {
        ...state,
        pads: updatedPads,
        activePads: updatedPads.filter(p => p.active === true),
        inactivePads: updatedPads.filter(p => p.active === false),
        padConfig: {
          ...state.padConfig,
          [pad]: {
            ...padConfig,
            id: pad,
            aircraft: 0
          }
        }
      };
    }
    case LINK_PAD_WITH_CHANNEL: {
      const { pad, channel } = action.payload;
      const padConfig = state.padConfig?.[pad] || {};
      return {
        ...state,
        padConfig: {
          ...state.padConfig,
          [pad]: {
            ...padConfig,
            id: pad,
            channel
          }
        }
      };
    }
    default:
      return state;
  }
};
const persistConfig = {
  key: 'live',
  storage
};
export const liveAircraftReducer = persistReducer(persistConfig, reducer);
